import React, { Ref, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import {
  CLOUD_TERMS_OF_SERVICE_URL,
  CLOUD_TERMS_OF_SERVICE_URL_MOBILE,
  PRIVACY_POLICY_URL,
  PRIVACY_POLICY_URL_MOBILE,
} from './links';
import { useLinkClickedEvent } from '../utilities/analytics/analytics-web-client';
import Link from '@atlaskit/link';

interface Props {
  isEmbedded: boolean;
  isMobileOidc: boolean;
  pageId: string;
  privacyPolicyText: PrivacyPolicyText;
}

const SignupLegal = styled.p`
  font-size: 12px;
`;

export enum PrivacyPolicyText {
  passwordSignup = 'passwordSignup',
  oauthSignup = 'oauthSignup',
}

const PrivacyPolicy = React.forwardRef(
  (
    { isEmbedded, isMobileOidc, pageId, privacyPolicyText }: Props,
    ref: Ref<HTMLParagraphElement>
  ) => {
    const cloudTOSLinkClicked = useLinkClickedEvent(pageId, 'cloudTermsOfService');
    const privacyPolicyLinkClicked = useLinkClickedEvent(pageId, 'privacyPolicy');

    const privacyPolicyMessage = useMemo(() => {
      const shouldUseMobileLinks = isEmbedded || isMobileOidc;
      const privacyPolicyUrl = shouldUseMobileLinks
        ? PRIVACY_POLICY_URL_MOBILE
        : PRIVACY_POLICY_URL;
      const cloudTermsOfServiceUrl = shouldUseMobileLinks
        ? CLOUD_TERMS_OF_SERVICE_URL_MOBILE
        : CLOUD_TERMS_OF_SERVICE_URL;

      const messageValues = {
        'cloud-tos-link': chunks => (
          <Link
            href={cloudTermsOfServiceUrl}
            target="_blank"
            rel="noreferrer noopener"
            onClick={cloudTOSLinkClicked}>
            {chunks}
          </Link>
        ),
        'privacy-policy-link': chunks => (
          <Link
            href={privacyPolicyUrl}
            target="_blank"
            rel="noreferrer noopener"
            onClick={privacyPolicyLinkClicked}>
            {chunks}
          </Link>
        ),
      };

      return privacyPolicyText === PrivacyPolicyText.passwordSignup ? (
        <FormattedMessage
          id="signup.form.legal.updated.password.xml"
          defaultMessage="By signing up, I accept the Atlassian <cloud-tos-link>Cloud Terms of Service</cloud-tos-link> and acknowledge the <privacy-policy-link>Privacy Policy</privacy-policy-link>."
          description="Cloud Terms of Service and Privacy Policy notice displayed to users when signing up for an Atlassian account"
          values={messageValues}
        />
      ) : (
        <FormattedMessage
          id="signup.form.legal.updated.Oauth.xml"
          defaultMessage="By creating an account, I accept the Atlassian <cloud-tos-link>Cloud Terms of Service</cloud-tos-link> and acknowledge the <privacy-policy-link>Privacy Policy</privacy-policy-link>."
          description="Cloud Terms of Service and Privacy Policy notice displayed to users when creating an Atlassian account"
          values={messageValues}
        />
      );
    }, [
      cloudTOSLinkClicked,
      privacyPolicyLinkClicked,
      isEmbedded,
      isMobileOidc,
      privacyPolicyText,
    ]);

    return (
      <SignupLegal data-testid="legal-message" ref={ref}>
        {privacyPolicyMessage}
      </SignupLegal>
    );
  }
);

PrivacyPolicy.displayName = 'PrivacyPolicy';

export default PrivacyPolicy;
